<template>
  <div class="p-8">
    <h1 class="text-2xl font-bold mb-6 flex items-center gap-2">
      Data Import &amp; Export
      <BaseInfoIcon :text="ERKLAERUNG.dateiImportExport" label="Data Import & Export" />
    </h1>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <!-- Export Section -->
      <div class="p-6 bg-white rounded-lg shadow-md">
        <h2 class="text-xl font-semibold mb-4">Export Data</h2>
        <p class="text-gray-600 mb-4">
          Click below to export all users and evaluations as a XLSX file.
        </p>
        <BaseButton @click="handleExport" :loading="isExporting">
          {{ isExporting ? "Exporting..." : "Export XLSX" }}
        </BaseButton>
      </div>

      <!-- Import Section -->
      <div class="p-6 bg-white rounded-lg shadow-md">
        <h2 class="text-xl font-semibold mb-4">Import Data</h2>
        <p class="text-gray-600 mb-4">
          The import replaces the database with the contents of the sheet.
          <b>Every row that is not in the file is deleted</b> — messages,
          ratings, users, criteria. Preview first to see what would be removed.
        </p>

        <input
          type="file"
          accept=".xlsx"
          @change="handleFileChange"
          class="mb-4"
        />

        <BaseButton
          variant="switch"
          :disabled="!selectedFile || isPreviewing"
          @click="handlePreview"
        >
          {{ isPreviewing ? "Checking..." : "Preview import" }}
        </BaseButton>

        <!-- Trockenlauf-Ergebnis. Erscheint erst, wenn der Server gezaehlt
             hat, und gehoert immer zu genau der Datei, die gerade gewaehlt
             ist (previewFor). -->
        <div v-if="preview" class="mt-4">
          <div
            v-if="preview.total_deletions > 0"
            class="bg-red-50 border border-red-200 text-red-800 rounded-md p-3 text-sm"
          >
            <p class="font-semibold">
              {{ preview.total_deletions }} rows would be deleted permanently.
            </p>
            <!-- Was ohne Archivkopie verschwindet, wird eigens genannt. Der
                 frühere Pauschalsatz „nothing is archived“ war seit dem
                 A13-Archivpfad unwahr und verdeckte gerade die Zeilen, um die
                 es wirklich geht.
                 ACHTUNG: der else-Zweig darf NICHT behaupten, Bewertungen
                 seien archiviert. Er greift auch, wenn das Backend das Feld
                 gar nicht liefert (undefined > 0 ist false) — eine
                 beruhigende Aussage wäre dann eine Aussage über etwas, das
                 die Seite nicht weiß. -->
            <p
              v-if="preview.unarchived_ratings > 0"
              class="mt-1 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-amber-900"
            >
              {{ preview.unarchived_ratings }} of them are message ratings
              deleted <b>without an archive copy</b> — model value, score
              source, model name and rating metadata are gone for good.
              Ratings whose criterion is removed are archived and stay
              recoverable; those are shown separately below.
            </p>
            <p v-else class="mt-1 text-red-700">
              Deleted rows cannot be restored, except message ratings that are
              explicitly marked as archived below.
            </p>
            <div class="mt-2 flex flex-wrap gap-1">
              <span
                v-for="step in preview.deletions"
                :key="step.step"
                class="px-2 py-0.5 rounded text-[11px] border"
                :class="
                  step.unarchived
                    ? 'bg-amber-100 text-amber-900 border-amber-300'
                    : step.archived
                    ? 'bg-emerald-100 text-emerald-900 border-emerald-300'
                    : 'bg-red-100 text-red-800 border-red-200'
                "
                :title="
                  step.archived
                    ? `archived as '${step.archived}'`
                    : step.unarchived
                    ? `${step.step} — deleted without an archive copy`
                    : step.step
                "
              >
                {{ step.step }}: {{ step.count }}
              </span>
            </div>
          </div>

          <div
            v-else
            class="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-md p-3 text-sm"
          >
            Nothing would be deleted. The sheet covers every existing row.
          </div>

          <p
            v-if="preview.protected_tables.length"
            class="mt-2 text-[11px] text-gray-500"
          >
            {{ preview.protected_tables.length }} step(s) skipped because the
            file does not contain the matching sheet — those tables stay
            untouched.
          </p>

          <label
            v-if="preview.total_deletions > 0"
            class="mt-3 flex items-start gap-2 text-sm text-gray-700"
          >
            <input type="checkbox" v-model="confirmed" class="mt-0.5" />
            <span>
              I understand that {{ preview.total_deletions }} rows will be
              deleted.
            </span>
          </label>

          <div class="mt-3">
            <BaseButton
              :variant="preview.total_deletions > 0 ? 'delete' : 'primary'"
              :disabled="!importAllowed"
              @click="handleImport"
            >
              {{ isImporting ? "Importing..." : "Confirm import" }}
            </BaseButton>
          </div>
        </div>

        <p v-if="message" class="mt-4 text-sm" :class="messageClass">
          {{ message }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import BaseButton from "@/BaseComponents/BaseButton.vue";
import BaseInfoIcon from "@/BaseComponents/BaseInfoIcon.vue";
import { ERKLAERUNG } from "@/async-sessions/erklaerungen";
import {
  exportUsersXLSX,
  importUsersXLSX,
  previewImportXLSX,
} from "@/live-sessions/api/files";

const selectedFile = ref(null);
const isExporting = ref(false);
const isImporting = ref(false);
const isPreviewing = ref(false);
const message = ref("");
const preview = ref(null);
const confirmed = ref(false);
// Kennung der Datei, zu der die Vorschau gehoert. Ohne sie koennte der Nutzer
// nach dem Trockenlauf eine ANDERE Datei waehlen und die Bestaetigung stuende
// dann fuer Zahlen, die zu dieser Datei nie erhoben wurden.
const previewFor = ref(null);

// Message color
const messageClass = computed(() =>
  message.value.includes("success") ? "text-green-600" : "text-red-600"
);

const fileKey = (f) => (f ? `${f.name}|${f.size}|${f.lastModified}` : null);

const importAllowed = computed(() => {
  if (!selectedFile.value || !preview.value || isImporting.value) return false;
  if (previewFor.value !== fileKey(selectedFile.value)) return false;
  // Ohne Loeschungen braucht es keine Bestaetigung -- der Haken waere dann
  // eine Huerde ohne Aussage.
  return preview.value.total_deletions === 0 || confirmed.value;
});

// Jede neue Datei entwertet die Vorschau. Sie stehen zu lassen waere der
// gefaehrlichste Zustand dieser Seite: gruene Zahlen zu einem anderen Blatt.
const resetPreview = () => {
  preview.value = null;
  previewFor.value = null;
  confirmed.value = false;
};

// Handle file input
const handleFileChange = (e) => {
  selectedFile.value = e.target.files[0];
  message.value = "";
  resetPreview();
};

// Trockenlauf
const handlePreview = async () => {
  if (!selectedFile.value) return;
  isPreviewing.value = true;
  message.value = "";
  resetPreview();

  const formData = new FormData();
  formData.append("file", selectedFile.value);

  try {
    const response = await previewImportXLSX(formData);
    preview.value = response.data;
    previewFor.value = fileKey(selectedFile.value);
  } catch (err) {
    console.error(err);
    message.value = "Could not read the file.";
  } finally {
    isPreviewing.value = false;
  }
};

// Export
const handleExport = async () => {
  isExporting.value = true;
  try {
    const response = await exportUsersXLSX();
    const url = window.URL.createObjectURL(new Blob([response.data]));

    // Generate timestamp in yyyy-mm-ddThh:mm format
    const now = new Date();
    const pad = (n) => n.toString().padStart(2, "0");
    const timestamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
    const filename = `rtaf_data_${timestamp}.xlsx`;

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (err) {
    message.value = "Error exporting users.";
  } finally {
    isExporting.value = false;
  }
};

// Import
const handleImport = async () => {
  // Der Waechter steht hier und nicht nur am :disabled des Knopfes. Ein
  // deaktivierter Knopf ist eine Anzeige, keine Sperre -- der Handler ist die
  // letzte Stelle vor einem Loeschvorgang, der zum Teil nicht archiviert wird
  // (preview.unarchived_ratings).
  if (!importAllowed.value) return;
  isImporting.value = true;
  const formData = new FormData();
  formData.append("file", selectedFile.value);

  try {
    await importUsersXLSX(formData);
    message.value = "Import completed successfully!";
    resetPreview();
  } catch (err) {
    console.error(err);
    message.value = "Error importing data.";
  } finally {
    isImporting.value = false;
  }
};
</script>

<style scoped>
input[type="file"] {
  display: block;
}
</style>
